import { useEffect, useRef } from 'react';
import type { DepthFrame, DepthLevel, StreamFrame } from '../../shared/contract';
import { basePath } from '../lib/paths';
import { log } from '../lib/log';
import { fmtPrice } from '../lib/format';
import { useStream } from '../hooks/useStream';
import { prefersReducedMotion } from '../hooks/useReducedMotion';
import depthSnapshot from '../data/depth.json';

const LEVELS = 7;
const SYMBOL = 'BTCUSDT';

export interface BookState {
  live: boolean;
  source: string;
}

interface BookProps {
  onState?: (state: BookState) => void;
}

function snapshotFrame(): DepthFrame | null {
  const raw = depthSnapshot as unknown as Record<string, DepthFrame>;
  const entry = raw[SYMBOL];
  if (!entry || !Array.isArray(entry.bids) || !Array.isArray(entry.asks)) return null;
  if (entry.bids.length === 0 || entry.asks.length === 0) return null;
  return entry;
}

interface RowRefs {
  price: HTMLSpanElement | null;
  size: HTMLSpanElement | null;
  bar: HTMLSpanElement | null;
}

export function Book({ onState }: BookProps) {
  const askRefs = useRef<RowRefs[]>(Array.from({ length: LEVELS }, () => ({ price: null, size: null, bar: null })));
  const bidRefs = useRef<RowRefs[]>(Array.from({ length: LEVELS }, () => ({ price: null, size: null, bar: null })));
  const midRef = useRef<HTMLSpanElement>(null);
  const spreadRef = useRef<HTMLSpanElement>(null);
  const imbalanceRef = useRef<HTMLSpanElement>(null);
  const imbalanceBarRef = useRef<HTMLSpanElement>(null);
  const frameRef = useRef<DepthFrame | null>(snapshotFrame());
  const dirtyRef = useRef(true);
  const widthsRef = useRef<number[]>(new Array(LEVELS * 2).fill(0));
  const stateRef = useRef(onState);
  stateRef.current = onState;

  useStream<StreamFrame>({
    path: basePath(`/stream/depth?symbol=${SYMBOL}`),
    label: 'book',
    onFrame: (frame) => {
      if (frame.type === 'status') {
        stateRef.current?.({
          live: frame.upstream === 'live',
          source: frame.upstream === 'live' ? frame.source : 'cached',
        });
        return;
      }
      if (frame.type !== 'depth') return;
      const depth = frame as DepthFrame;
      if (!Array.isArray(depth.bids) || !Array.isArray(depth.asks)) return;
      if (depth.bids.length === 0 || depth.asks.length === 0) return;
      frameRef.current = depth;
      dirtyRef.current = true;
      stateRef.current?.({ live: depth.source !== 'snapshot', source: depth.source });
    },
  });

  useEffect(() => {
    let raf = 0;
    const reduced = prefersReducedMotion();
    let painted = false;

    const writeText = (frame: DepthFrame) => {
      const asks = frame.asks.slice(0, LEVELS);
      const bids = frame.bids.slice(0, LEVELS);

      for (let i = 0; i < LEVELS; i += 1) {
        const ask = asks[LEVELS - 1 - i] as DepthLevel | undefined;
        const row = askRefs.current[i];
        if (row?.price) row.price.textContent = ask ? fmtPrice(ask.price, 1) : '—';
        if (row?.size) row.size.textContent = ask ? ask.size.toFixed(3) : '—';
      }
      for (let i = 0; i < LEVELS; i += 1) {
        const bid = bids[i] as DepthLevel | undefined;
        const row = bidRefs.current[i];
        if (row?.price) row.price.textContent = bid ? fmtPrice(bid.price, 1) : '—';
        if (row?.size) row.size.textContent = bid ? bid.size.toFixed(3) : '—';
      }

      if (midRef.current) midRef.current.textContent = fmtPrice(frame.mid, 1);
      if (spreadRef.current) spreadRef.current.textContent = `${frame.spreadBps.toFixed(2)} bps`;

      const bidSum = bids.reduce((acc, l) => acc + l.size, 0);
      const askSum = asks.reduce((acc, l) => acc + l.size, 0);
      const total = bidSum + askSum;
      const ratio = total > 0 ? bidSum / total : 0.5;
      if (imbalanceRef.current) {
        imbalanceRef.current.textContent = `${(ratio * 100).toFixed(1)}% bid`;
        imbalanceRef.current.dataset.dir = ratio >= 0.5 ? 'up' : 'down';
      }
      if (imbalanceBarRef.current) {
        imbalanceBarRef.current.style.width = `${(ratio * 100).toFixed(1)}%`;
        imbalanceBarRef.current.dataset.dir = ratio >= 0.5 ? 'up' : 'down';
      }
    };

    const step = () => {
      const frame = frameRef.current;
      if (frame) {
        if (dirtyRef.current || !painted) {
          writeText(frame);
          dirtyRef.current = false;
          painted = true;
        }

        const asks = frame.asks.slice(0, LEVELS);
        const bids = frame.bids.slice(0, LEVELS);

        const askCum: number[] = [];
        const bidCum: number[] = [];
        let runningAsk = 0;
        let runningBid = 0;
        for (let j = 0; j < LEVELS; j += 1) {
          runningAsk += asks[j]?.size ?? 0;
          runningBid += bids[j]?.size ?? 0;
          askCum[j] = runningAsk;
          bidCum[j] = runningBid;
        }
        const peak = Math.max(runningAsk, runningBid, 1e-9);

        for (let i = 0; i < LEVELS; i += 1) {
          const depth = askCum[LEVELS - 1 - i] ?? 0;
          const target = Math.min(100, (depth / peak) * 100);
          const current = widthsRef.current[i] ?? 0;
          const next = reduced ? target : current + (target - current) * 0.22;
          widthsRef.current[i] = next;
          const bar = askRefs.current[i]?.bar;
          if (bar) bar.style.width = `${next.toFixed(1)}%`;
        }
        for (let i = 0; i < LEVELS; i += 1) {
          const depth = bidCum[i] ?? 0;
          const target = Math.min(100, (depth / peak) * 100);
          const idx = LEVELS + i;
          const current = widthsRef.current[idx] ?? 0;
          const next = reduced ? target : current + (target - current) * 0.22;
          widthsRef.current[idx] = next;
          const bar = bidRefs.current[i]?.bar;
          if (bar) bar.style.width = `${next.toFixed(1)}%`;
        }
      }
      raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    log.debug('demo', 'book render loop started');

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
      } else if (!raf) {
        raf = requestAnimationFrame(step);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const rows = Array.from({ length: LEVELS }, (_, i) => i);

  return (
    <div className="v5-book">
      <div className="v5-book-side" aria-hidden="true">
        {rows.map((i) => (
          <div className="v5-book-row v5-book-ask" key={`a${i}`}>
            <span
              className="v5-book-bar"
              ref={(el) => {
                const row = askRefs.current[i];
                if (row) row.bar = el;
              }}
            />
            <span
              className="v5-book-price"
              ref={(el) => {
                const row = askRefs.current[i];
                if (row) row.price = el;
              }}
            />
            <span
              className="v5-book-size"
              ref={(el) => {
                const row = askRefs.current[i];
                if (row) row.size = el;
              }}
            />
          </div>
        ))}
      </div>

      <div className="v5-book-mid">
        <span className="v5-book-midprice" ref={midRef} />
        <span className="v5-book-spread" ref={spreadRef} />
      </div>

      <div className="v5-book-side" aria-hidden="true">
        {rows.map((i) => (
          <div className="v5-book-row v5-book-bid" key={`b${i}`}>
            <span
              className="v5-book-bar"
              ref={(el) => {
                const row = bidRefs.current[i];
                if (row) row.bar = el;
              }}
            />
            <span
              className="v5-book-price"
              ref={(el) => {
                const row = bidRefs.current[i];
                if (row) row.price = el;
              }}
            />
            <span
              className="v5-book-size"
              ref={(el) => {
                const row = bidRefs.current[i];
                if (row) row.size = el;
              }}
            />
          </div>
        ))}
      </div>

      <div className="v5-book-foot">
        <span className="v5-book-imbalance" ref={imbalanceRef} />
        <span className="v5-book-imbar" aria-hidden="true">
          <span className="v5-book-imfill" ref={imbalanceBarRef} />
        </span>
      </div>
    </div>
  );
}
